/** @format */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "antd";
import styles from "../../../styles/Components/trading-assistant/tradeAssistantCards.module.sass";
import { FaCoins } from "react-icons/fa";
import AiAssistantTableActionButtons from "./AiAssistantTableActionButtons";
import OtherCoins from "../../../shared/other-coins-signal/OtherCoins";
import TradeAssistantSkeletonCard from "./TradeAssistantSkeletonCard";
import TradeAssistantCard from "./TradeAssistantCard";
import TradeAssistantList from "./TradeAssistantList";
import TradeAssistantTable from "./TradeAssistantTable";
import useRefreshSignals from "../../../hooks/api/useRefreshSignals";
import useSortedSignals from "../../../hooks/api/ai-assistant/useSortedSignals";
import usePriceRefresh from "../../../hooks/api/ai-assistant/usePriceRefresh";
import EmptyBox from "../../../ui/EmptyBox";
import ReportBoard from "../../../shared/ReportBoard";
import { getAssistantReport, getTradeAssistant } from "../../../redux/trading-assistant/actions";
import useServiceStatus from "../../../hooks/useServiceStatus";
import {
  liveSuggestionFor,
  normalizeLiveTimeframe,
  pivotsSettled,
} from "../../../redux/live-suggestion/liveSuggestionSlice";
import useLiveSuggestionRefresh from "../../../hooks/api/ai-assistant/useLiveSuggestionRefresh";
import usePowerfulSignalNotice from "../../../hooks/usePowerfulSignalNotice";
import { BOARD_QUESTIONS, DEEP_LINKS, PRICE_EXAMPLES, openGuideChat } from "../../../shared/GuideChat";
import { isStockBoard, quoteCards, sortStockCards, stockBoardCards, stockPivotTargets } from "../../../shared/stockBoards";
import { loadExtraSymbols } from "../../../shared/extraSymbols";
import AddBoardSymbol from "../../../shared/AddBoardSymbol";

function stockBoardVisual(rows, suggestions, macro) {
  const actions = { Buy: 0, Sell: 0, Hold: 0 };
  const symbols = (rows || []).map((row) => {
    const live = liveSuggestionFor(suggestions, row.SYMBOL_NAME, "1d");
    const side = live?.prediction;
    if (side === "Buy" || side === "Sell" || side === "Hold") actions[side] += 1;
    return { symbol: row.SYMBOL_NAME, action: side || "" };
  });
  return {
    actions,
    symbols,
    rows: symbols,
    live_timeframe: "1d",
    macro_regime: macro || null,
  };
}

const RenderEmptySignals = () => {
  return (
    <div className={styles.emptyBox}>
      <EmptyBox useCase="analytic" size={40}></EmptyBox>
      <h4 style={{ fontWeight: 600 }}>No signals found. Check back soon.</h4>
    </div>
  );
};

function liveForSymbol(suggestions, symbolName, timeframe) {
  return liveSuggestionFor(suggestions, symbolName, timeframe);
}

function liveLabel(prediction, confidence) {
  const side = String(prediction || "").split("/")[0];
  if (!side) return "";
  if (side === "Hold") return "Hold";
  const n = Number(confidence);
  const pct = Number.isFinite(n) ? (n > 1 ? n : n * 100) : 0;
  return `${pct >= 75 ? "Strong" : "Weak"} ${side}`;
}

function buildCardSnapshot(boardRows, suggestions, timeframe) {
  const liveTimeframe = normalizeLiveTimeframe(timeframe);
  return (boardRows || [])
    .filter((row) => row?.SYMBOL_NAME)
    .map((row) => {
      const live = liveForSymbol(suggestions, row.SYMBOL_NAME, liveTimeframe);
      const boardAction = String(row.TRADE_TYPE || "Hold").split("/")[0];
      const liveSide = live?.prediction
        ? String(live.prediction).split("/")[0]
        : "";
      const boardPivot = row.EVIDENCE?.pivot || {};
      const livePresent = Boolean(live && live.prediction);
      return {
        symbol: row.SYMBOL_NAME,
        board_action: boardAction,
        live_side: liveSide,
        live_confidence: live?.confidence ?? null,
        live_label: livePresent ? liveLabel(liveSide, live.confidence) : "",
        pivot_detected: livePresent
          ? liveSide === "Buy" || liveSide === "Sell"
          : Boolean(boardPivot.detected),
        pivot_timeframe: livePresent ? liveTimeframe : boardPivot.timeframe || "",
      };
    });
}

function applySelectedLive(visual, boardRows, suggestions, timeframe, applyLive = true) {
  const liveTimeframe = normalizeLiveTimeframe(timeframe);
  if (!applyLive) return visual || {};
  const snapshot = buildCardSnapshot(boardRows, suggestions, liveTimeframe);
  const byCoin = {};
  snapshot.forEach((item) => {
    const key = String(item.symbol || "").split("/")[0].toUpperCase();
    if (key) byCoin[key] = item;
  });
  const patch = (symbol, row) => {
    const snap = byCoin[String(symbol || "").split("/")[0].toUpperCase()];
    if (!snap) return row;
    return {
      ...row,
      live_side: snap.live_side || "",
      live_label: snap.live_label || "",
      live_confidence: snap.live_confidence,
      pivot: snap.pivot_detected ? "on" : "off",
    };
  };
  return {
    ...(visual || {}),
    live_timeframe: liveTimeframe,
    symbols: Array.isArray(visual?.symbols)
      ? visual.symbols.map((row) => patch(row.symbol, row))
      : visual?.symbols,
    rows: Array.isArray(visual?.rows)
      ? visual.rows.map((row) => patch(row.coin, row))
      : visual?.rows,
  };
}

const TradeAssistantCards = () => {
  const {
    tradingAssistant: {
      getTradeAssistantLoading,
      getAssistantReportLoading,
      tradeAssistant,
      assistantReport,
      assistantReportError,
      error: suggestionError,
      aiMarketType,
      allTradeSuggestionData,
      viewType,
      selectedPortfolio,
      aiAssistantMode,
    },
    main: { userData },
    liveSuggestion: { suggestions, liveTimeframe, coinStatus, macroRegime, usPreset },
    otherCoinsAnalyze: { analyze },
  } = useSelector((state) => state);

  const dispatch = useDispatch();
  const { isActive: suggestionsActive } = useServiceStatus("aiAssistant");
  const selectedLiveTimeframe = normalizeLiveTimeframe(liveTimeframe);
  const [extraTick, setExtraTick] = useState(0);
  const extraSymbols = useMemo(
    () => loadExtraSymbols(aiMarketType),
    [aiMarketType, extraTick],
  );
  const stockBoard = isStockBoard(aiMarketType);
  const stockRows = stockBoard ? stockBoardCards(aiMarketType, extraSymbols, usPreset) : [];
  const forexExtras = aiMarketType === "forex" ? quoteCards(extraSymbols) : [];
  const pivotTargets = useMemo(() => {
    if (stockBoard) return stockPivotTargets(aiMarketType, extraSymbols, usPreset);
    if (aiMarketType === "forex") {
      return extraSymbols.map((symbol) => ({
        symbol,
        exchange: "yahoo",
        timeframe: "1d",
        stock: true,
      }));
    }
    if (aiMarketType !== "crypto") return [];
    const seen = new Set();
    const targets = [];
    [...(tradeAssistant?.ASSISTANT_SUGGESTIONS || []), ...(analyze || [])].forEach(
      (item) => {
        const symbol = item?.SYMBOL_NAME;
        if (!symbol) return;
        const key = String(symbol).split("/")[0].toUpperCase();
        if (seen.has(key)) return;
        seen.add(key);
        targets.push({
          symbol,
          exchange: item?.AVAILABLE_EXCHANGES?.[0] || item?.EXCHANGE_ID || "kucoin",
        });
      },
    );
    return targets;
  }, [aiMarketType, analyze, extraSymbols, stockBoard, tradeAssistant?.ASSISTANT_SUGGESTIONS, usPreset]);
  useLiveSuggestionRefresh(
    aiMarketType === "crypto" || stockBoard || aiMarketType === "forex",
    pivotTargets,
  );
  const pivotsReady = pivotsSettled(
    pivotTargets,
    suggestions,
    coinStatus,
    selectedLiveTimeframe,
  );
  const stopLossCFC = tradeAssistant?.coefficients?.stop_loss;
  const takeProfitCFC = tradeAssistant?.coefficients?.take_profit;

  const sortedCardsData = useSortedSignals({
    data: tradeAssistant?.ASSISTANT_SUGGESTIONS,
    allTradeSuggestionData,
    analyze,
    suggestions,
    aiMarketType,
  });

  const [cardsData, setCardsData] = useState([]);

  const displayRows = stockBoard
    ? sortStockCards(stockRows, suggestions)
    : [
        ...(cardsData?.length
          ? cardsData
          : sortedCardsData?.length
            ? sortedCardsData
            : []),
        ...forexExtras,
      ];

  const symbols = displayRows
    ?.filter((item) => item?.SYMBOL_NAME)
    ?.map((item) => item?.SYMBOL_NAME?.toLowerCase()?.split("/usdt")?.[0]);

  useEffect(() => {
    if (!Array.isArray(tradeAssistant?.ASSISTANT_SUGGESTIONS) || tradeAssistant.ASSISTANT_SUGGESTIONS.length === 0) {
      setCardsData([]);
    } else {
      setCardsData(sortedCardsData);
    }
  }, [sortedCardsData, tradeAssistant?.ASSISTANT_SUGGESTIONS]);

  // Update prices every 60s
  usePriceRefresh({
    cardsData,
    stopLossCFC,
    takeProfitCFC,
    userData,
    setCardsData,
    active: aiMarketType === "crypto",
  });

  useRefreshSignals("ai");

  usePowerfulSignalNotice({
    kind: "assistant",
    page: "AI Assistant",
    boardKey:
      tradeAssistant?.board_id ||
      `${selectedPortfolio || ""}|${aiMarketType || ""}`,
    rows: stockBoard ? stockRows : cardsData,
    suggestions,
    timeframe: stockBoard ? "1d" : selectedLiveTimeframe,
  });

  const snapshotRows = cardsData?.length
    ? cardsData
    : tradeAssistant?.ASSISTANT_SUGGESTIONS;

  const hasUsableBoard = Array.isArray(tradeAssistant?.ASSISTANT_SUGGESTIONS) &&
    tradeAssistant.ASSISTANT_SUGGESTIONS.length > 0;
  const boardSettled = Boolean(tradeAssistant) && !Array.isArray(tradeAssistant);
  const suggestionFailed = Boolean(suggestionError);
  const showSignalLoading = stockBoard
    ? false
    : getTradeAssistantLoading || (!boardSettled && !suggestionFailed);
  const showSignalFailure = stockBoard
    ? false
    : !showSignalLoading && suggestionFailed && !hasUsableBoard;
  const showSignalEmpty = stockBoard
    ? false
    : !showSignalLoading && !suggestionFailed && boardSettled && !hasUsableBoard;
  const refreshFailed = !showSignalLoading && suggestionFailed && hasUsableBoard;
  const hideBoardSummary = stockBoard || showSignalLoading || showSignalFailure || showSignalEmpty;
  const waitingForPivots = hasUsableBoard && !stockBoard && !pivotsReady;
  const boardStatusNote = showSignalLoading
    ? "Loading signals…"
    : showSignalFailure
      ? "Signals didn't load. Try again."
      : refreshFailed
        ? "Signals didn't refresh. Try again."
        : showSignalEmpty
          ? "No signals found. Check back soon."
          : waitingForPivots
            ? "Waiting for pivots…"
            : "";
  const reportContextKey = [
    (selectedPortfolio || "primary").toLowerCase(),
    aiMarketType || "crypto",
    aiAssistantMode || "",
    tradeAssistant?.board_id ||
      tradeAssistant?.generated_at ||
      tradeAssistant?.signal_timestamp ||
      "",
  ].join("|");
  const requestedReportRef = useRef("");
  useEffect(() => {
    if (!suggestionsActive || stockBoard || !userData?.email || !hasUsableBoard || !pivotsReady) return;
    if (assistantReport?.assistant_summary?.text &&
      assistantReport?.report_board_id &&
      tradeAssistant?.board_id &&
      assistantReport.report_board_id === tradeAssistant.board_id) {
      requestedReportRef.current = reportContextKey;
      return;
    }
    if (requestedReportRef.current === reportContextKey) return;
    if (getAssistantReportLoading) {
      requestedReportRef.current = reportContextKey;
      return;
    }
    requestedReportRef.current = reportContextKey;
    dispatch(
      getAssistantReport(
        userData?.["cognito:username"],
        userData?.["custom:custom_username"],
        (selectedPortfolio || "primary").toLowerCase(),
        aiMarketType,
        aiAssistantMode,
        false,
        buildCardSnapshot(snapshotRows, suggestions, selectedLiveTimeframe),
      ),
    );
  }, [
    userData,
    hasUsableBoard,
    reportContextKey,
    assistantReport?.assistant_summary,
    getAssistantReportLoading,
    dispatch,
    selectedPortfolio,
    aiMarketType,
    aiAssistantMode,
    suggestions,
    snapshotRows,
    pivotsReady,
    suggestionsActive,
  ]);

  const loadSuggestions = () => {
    if (!suggestionsActive || getTradeAssistantLoading || !userData?.["cognito:username"]) return;
    dispatch(
      getTradeAssistant(
        userData["cognito:username"],
        userData?.["custom:custom_username"],
        (selectedPortfolio || "primary").toLowerCase(),
        aiMarketType,
        suggestionsActive,
        aiAssistantMode,
      ),
    );
  };

  const refreshReport = () => {
    if (!suggestionsActive || !userData?.email || getAssistantReportLoading || showSignalLoading || !pivotsReady) return;
    dispatch(
      getAssistantReport(
        userData?.["cognito:username"],
        userData?.["custom:custom_username"],
        (selectedPortfolio || "primary").toLowerCase(),
        aiMarketType,
        aiAssistantMode,
        true,
        buildCardSnapshot(snapshotRows, suggestions, selectedLiveTimeframe),
      ),
    );
  };

  const reportOutdated = Boolean(
    assistantReport?.report_board_id &&
      tradeAssistant?.board_id &&
      assistantReport.report_board_id !== tradeAssistant.board_id,
  );

  return (
    <div className={styles.container}>
      <AiAssistantTableActionButtons />
      <ReportBoard
        variant="assistant"
        summary={assistantReport?.assistant_summary}
        exportLinks={assistantReport?.export_links}
        visualSummary={
          stockBoard
            ? stockBoardVisual(stockRows, suggestions, macroRegime)
            : {
                ...applySelectedLive(
                  {
                    ...(tradeAssistant?.visual_summary || {}),
                    ...(assistantReport?.visual_summary
                      ? {
                          symbols: assistantReport.visual_summary.symbols?.length
                            ? assistantReport.visual_summary.symbols
                            : tradeAssistant?.visual_summary?.symbols,
                          pivots: assistantReport.visual_summary.pivots?.length
                            ? assistantReport.visual_summary.pivots
                            : tradeAssistant?.visual_summary?.pivots,
                          live_conflicts: assistantReport.visual_summary.live_conflicts,
                        }
                      : {}),
                  },
                  snapshotRows,
                  suggestions,
                  selectedLiveTimeframe,
                  aiMarketType === "crypto",
                ),
                macro_regime: macroRegime,
              }
        }
        freshness={{
          generated_at: tradeAssistant?.generated_at,
          data_as_of: tradeAssistant?.data_as_of,
          next_refresh_at: tradeAssistant?.next_refresh_at,
        }}
        onRequest={showSignalLoading || hasUsableBoard ? refreshReport : undefined}
        loading={showSignalLoading || getAssistantReportLoading}
        error={!hideBoardSummary && Boolean(assistantReportError)}
        disabled={showSignalLoading || !hasUsableBoard || waitingForPivots}
        outdated={!hideBoardSummary && reportOutdated}
        statusNote={boardStatusNote}
        statusError={showSignalFailure || refreshFailed}
        hideSummary={hideBoardSummary}
        actionLabel={
          showSignalLoading
            ? "Loading signals…"
            : waitingForPivots
              ? "Waiting for pivots…"
              : getAssistantReportLoading
                ? "Generating AI report…"
                : assistantReport?.assistant_summary?.text
                  ? "Refresh AI report"
                  : "Load AI report"
        }
        leading={
          <div className={styles.boardQuestions}>
            <p>Ask AI</p>
            {DEEP_LINKS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={item.id === "markets" ? styles.deepMarkets : styles.deepWeekly}
                title={item.hint}
                onClick={() => openGuideChat({ deep: item.id })}
              >
                {item.icon ? <item.icon /> : null}
                {item.label}
              </button>
            ))}
            {BOARD_QUESTIONS.map((item) => (
              <button
                key={item.ask}
                type="button"
                className={styles.curious}
                title={item.ask}
                onClick={() => openGuideChat(item.ask)}
              >
                {item.icon ? <item.icon /> : null}
                {item.label}
              </button>
            ))}
          </div>
        }
        more={
          <div className={styles.boardQuestions}>
            <p>Price and daily examples</p>
            {PRICE_EXAMPLES.map((item) => (
              <button
                key={item.ask}
                type="button"
                title={item.ask}
                onClick={() => openGuideChat(item.ask)}
              >
                {item.icon ? <item.icon /> : null}
                {item.label}
              </button>
            ))}
          </div>
        }
      />

      {viewType === "card" || stockBoard ? (
        <div className={styles.cardsGrid}>
          {showSignalLoading && !hasUsableBoard ? (
            [...Array(6)].map((_, index) => (
              <TradeAssistantSkeletonCard key={index} />
            ))
          ) : showSignalFailure ? (
            <div className={styles.emptyBox}>
              <EmptyBox useCase="analytic" size={40} />
              <h4 style={{ fontWeight: 600 }}>Signals didn't load. Try again.</h4>
              <Button type="primary" onClick={loadSuggestions}>
                Try again
              </Button>
            </div>
          ) : showSignalEmpty ? (
            <>
              <RenderEmptySignals />
              {aiMarketType === "crypto" && (
                <div className={`${styles.card} ${styles.otherCoins}`}>
                  <OtherCoins
                    className={styles.otherCoinsBtn}
                    disabledCoins={symbols}
                    active
                  >
                    <h3>
                      <FaCoins />
                      Choose any Coin
                    </h3>
                    <p>Add new coin from any exchange to see AI suggestion!</p>
                  </OtherCoins>
                </div>
              )}
              <AddBoardSymbol
                market={aiMarketType}
                existing={stockRows.map((row) => row.SYMBOL_NAME)}
                onChange={() => setExtraTick((value) => value + 1)}
              />
            </>
          ) : (
            <>
              {displayRows.map((item, index) => (
                <TradeAssistantCard data={item} key={index} />
              ))}
              {aiMarketType === "crypto" && (
                <div className={`${styles.card} ${styles.otherCoins}`}>
                  <OtherCoins
                    className={styles.otherCoinsBtn}
                    disabledCoins={symbols}
                    active
                  >
                    <h3>
                      <FaCoins />
                      Choose any Coin
                    </h3>
                    <p>Add new coin from any exchange to see AI suggestion!</p>
                  </OtherCoins>
                </div>
              )}
              <AddBoardSymbol
                market={aiMarketType}
                existing={displayRows.map((row) => row.SYMBOL_NAME)}
                onChange={() => setExtraTick((value) => value + 1)}
              />
            </>
          )}
        </div>
      ) : (
        <>
          {showSignalLoading && !hasUsableBoard ? (
            <div className={styles.loadingContainer}>
              {[...Array(3)].map((_, index) => (
                <TradeAssistantSkeletonCard key={index} />
              ))}
            </div>
          ) : showSignalFailure ? (
            <div className={styles.emptyBox}>
              <EmptyBox useCase="analytic" size={40} />
              <h4 style={{ fontWeight: 600 }}>Signals didn't load. Try again.</h4>
              <Button type="primary" onClick={loadSuggestions}>
                Try again
              </Button>
            </div>
          ) : showSignalEmpty ? (
            <>
              <RenderEmptySignals />
              {aiMarketType === "crypto" && (
                <div
                  className={`${styles.card} ${styles.otherCoins}`}
                  style={{ marginTop: "1rem" }}
                >
                  <OtherCoins
                    className={styles.otherCoinsBtn}
                    disabledCoins={symbols}
                  >
                    <h3>
                      <FaCoins />
                      Choose any Coin
                    </h3>
                    <p>Add new coin from any exchange to see AI suggestion!</p>
                  </OtherCoins>
                </div>
              )}
              <AddBoardSymbol
                market={aiMarketType}
                existing={displayRows.map((row) => row.SYMBOL_NAME)}
                onChange={() => setExtraTick((value) => value + 1)}
              />
            </>
          ) : (
            <>
              <TradeAssistantTable data={displayRows} />
              {aiMarketType === "crypto" && (
                <div
                  className={`${styles.card} ${styles.otherCoins}`}
                  style={{ marginTop: "1rem" }}
                >
                  <OtherCoins
                    className={styles.otherCoinsBtn}
                    disabledCoins={symbols}
                  >
                    <h3>
                      <FaCoins />
                      Choose any Coin
                    </h3>
                    <p>Add new coin from any exchange to see AI suggestion!</p>
                  </OtherCoins>
                </div>
              )}
              <AddBoardSymbol
                market={aiMarketType}
                existing={displayRows.map((row) => row.SYMBOL_NAME)}
                onChange={() => setExtraTick((value) => value + 1)}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default TradeAssistantCards;
